/**
 * CSRGroupsPage — Reynolds 2006 functional-group classification
 *
 * Predicts the dominant phytoplankton CODON (e.g. C, S2, R3...) given mixing
 * energy, light availability, P, N, temperature. Augments the existing
 * diatoms/greens/cyanobacteria simulation with taxonomic resolution.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { FlaskConical, ArrowLeft, Info, AlertTriangle } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

// Reynolds 2006 codons we score against (12 most common in temperate reservoirs)
interface Codon {
  code: string;
  name: string;
  habitat: string;
  // Trait scores 0-1
  light_tolerance: number;
  mix_tolerance: number;
  nutrient_demand: number;     // 0 = low, 1 = high
  temp_optimum_C: number;
  toxic: boolean;
}

const CODONS: Codon[] = [
  { code: 'A',   name: 'Cyclotella (small diatoms)',   habitat: 'oligotrophic, mixed',         light_tolerance: 0.8, mix_tolerance: 0.9, nutrient_demand: 0.2, temp_optimum_C: 8,  toxic: false },
  { code: 'B',   name: 'Aulacoseira',                  habitat: 'mesotrophic, mixed',           light_tolerance: 0.6, mix_tolerance: 0.9, nutrient_demand: 0.5, temp_optimum_C: 12, toxic: false },
  { code: 'C',   name: 'Asterionella, Stephanodiscus',habitat: 'eutrophic, mixed Si',          light_tolerance: 0.5, mix_tolerance: 0.8, nutrient_demand: 0.7, temp_optimum_C: 14, toxic: false },
  { code: 'D',   name: 'Synedra',                      habitat: 'eutrophic, enriched mixed',    light_tolerance: 0.4, mix_tolerance: 0.7, nutrient_demand: 0.8, temp_optimum_C: 16, toxic: false },
  { code: 'J',   name: 'Pediastrum, Scenedesmus',      habitat: 'hyper-eutrophic shallow',      light_tolerance: 0.6, mix_tolerance: 0.5, nutrient_demand: 0.9, temp_optimum_C: 22, toxic: false },
  { code: 'K',   name: 'Aphanocapsa, Aphanothece',     habitat: 'shallow eutrophic',            light_tolerance: 0.5, mix_tolerance: 0.4, nutrient_demand: 0.7, temp_optimum_C: 22, toxic: false },
  { code: 'L_O', name: 'Peridinium, Ceratium',         habitat: 'mesotrophic stratified',       light_tolerance: 0.7, mix_tolerance: 0.3, nutrient_demand: 0.4, temp_optimum_C: 18, toxic: false },
  { code: 'L_M', name: 'Microcystis aeruginosa',       habitat: 'eutrophic stratified, calm',   light_tolerance: 0.5, mix_tolerance: 0.1, nutrient_demand: 0.8, temp_optimum_C: 24, toxic: true },
  { code: 'M',   name: 'Microcystis (bloom-forming)',  habitat: 'hyper-eutrophic calm',         light_tolerance: 0.4, mix_tolerance: 0.1, nutrient_demand: 0.9, temp_optimum_C: 25, toxic: true },
  { code: 'P',   name: 'Aulacoseira, Closterium',      habitat: 'eutrophic, continuous mixing', light_tolerance: 0.5, mix_tolerance: 0.95, nutrient_demand: 0.8, temp_optimum_C: 14, toxic: false },
  { code: 'S1',  name: 'Planktothrix agardhii',        habitat: 'turbid eutrophic',             light_tolerance: 0.3, mix_tolerance: 0.5, nutrient_demand: 0.8, temp_optimum_C: 20, toxic: true },
  { code: 'X1',  name: 'Chlorella, Monoraphidium',     habitat: 'enriched shallow',             light_tolerance: 0.7, mix_tolerance: 0.6, nutrient_demand: 0.8, temp_optimum_C: 22, toxic: false },
];

interface Forcing { T: number; light: number; P: number; mixing: number; }

function scoreCodon(c: Codon, f: Forcing): number {
  const f_T = Math.max(0, Math.exp(-Math.pow((f.T - c.temp_optimum_C) / 8, 2)));
  const f_light = c.light_tolerance * f.light + (1 - c.light_tolerance) * (1 - f.light);
  const f_nutrient = c.nutrient_demand * f.P + (1 - c.nutrient_demand) * (1 - f.P);
  const f_mix = c.mix_tolerance * f.mixing + (1 - c.mix_tolerance) * (1 - f.mixing);
  return f_T * Math.max(0.1, f_light) * Math.max(0.1, f_nutrient) * Math.max(0.1, f_mix);
}

export default function CSRGroupsPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('csr_groups');
  const [T, setT] = useState(18);
  const [light, setLight] = useState(0.7);
  const [P, setP] = useState(0.5);
  const [mixing, setMixing] = useState(0.3);

  const ranked = useMemo(() => {
    const f = { T, light, P, mixing };
    return CODONS.map(c => ({ c, score: scoreCodon(c, f) })).sort((a, b) => b.score - a.score);
  }, [T, light, P, mixing]);

  const dominant = ranked[0];
  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={FlaskConical} title="Module — Reynolds CSR Functional Groups" subtitle="Trait-based phytoplankton classification (12 dominant codons)"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('csr_groups')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="csr_groups" />

      <div className="rounded-xl border-2 border-violet-700 bg-violet-900/15 p-4">
        <div className="flex items-center gap-2 mb-2">
          {dominant.c.toxic && <AlertTriangle className="w-5 h-5 text-red-400" />}
          <div>
            <div className="text-xs text-violet-300 uppercase tracking-wider">Predicted dominant codon</div>
            <div className="text-2xl font-bold text-slate-100">{dominant.c.code} — {dominant.c.name}</div>
            <div className="text-xs text-slate-400 mt-0.5">{dominant.c.habitat}{dominant.c.toxic ? ' · ⚠ toxin-producing' : ''}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Water T: <strong className="text-slate-100">{T} °C</strong></label>
          <input type="range" min={4} max={28} step={0.5} value={T} onChange={e => setT(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Light availability: <strong className="text-slate-100">{(light * 100).toFixed(0)}%</strong></label>
          <input type="range" min={0} max={1} step={0.05} value={light} onChange={e => setLight(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">P availability: <strong className="text-slate-100">{(P * 100).toFixed(0)}%</strong></label>
          <input type="range" min={0} max={1} step={0.05} value={P} onChange={e => setP(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Mixing energy: <strong className="text-slate-100">{(mixing * 100).toFixed(0)}%</strong></label>
          <input type="range" min={0} max={1} step={0.05} value={mixing} onChange={e => setMixing(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Functional-group ranking</div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead className="bg-slate-800">
              <tr><th className="px-2 py-1 text-left text-slate-300">Codon</th><th className="px-2 py-1 text-left text-slate-300">Genera</th><th className="px-2 py-1 text-left text-slate-300">Habitat</th><th className="px-2 py-1 text-right text-slate-300">T_opt</th><th className="px-2 py-1 text-right text-slate-300">Score</th><th className="px-2 py-1 text-center text-slate-300">Tox</th></tr>
            </thead>
            <tbody>
              {ranked.slice(0, 10).map((r, i) => (
                <tr key={r.c.code} className={`border-b border-slate-800 ${i === 0 ? 'bg-violet-900/20' : ''}`}>
                  <td className="px-2 py-1 text-slate-100 font-mono">{r.c.code}</td>
                  <td className="px-2 py-1 text-slate-100">{r.c.name}</td>
                  <td className="px-2 py-1 text-slate-400">{r.c.habitat}</td>
                  <td className="px-2 py-1 text-right text-slate-300">{r.c.temp_optimum_C} °C</td>
                  <td className="px-2 py-1 text-right text-slate-100 tabular-nums font-bold">{fmt(r.score, 3)}</td>
                  <td className="px-2 py-1 text-center">{r.c.toxic ? <span className="text-red-400 font-bold">●</span> : <span className="text-slate-600">○</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> About CSR codons</div>
        <p>Reynolds 2006 classified phytoplankton into ~38 functional groups (codons) based on ecological
        traits — not strict taxonomy. Each codon shares life-history strategy under specific habitat
        conditions. The model above scores 12 dominant temperate-reservoir codons against four forcing
        variables and identifies the codon expected to dominate.</p>
        <p className="mt-2">When enabled, the Algal Dynamics page can cross-reference its lumped diatoms/
        greens/cyanobacteria output against this taxonomic prediction. If the lumped model says "cyanobacteria
        dominate" and the CSR model picks codon M (Microcystis), the diagnosis is consistent and high-confidence.</p>
        <p className="italic text-slate-500 mt-2">References: Reynolds 2006 (The Ecology of Phytoplankton); Reynolds, Huszar et al. 2002 Funct. Ecol. 16, 503–515.</p>
      </div>
    </div>
  );
}
